import { all, put, takeLatest, select } from 'redux-saga/effects';

import { SCIApi } from 'src/apis';
import { useBookingSummaryKey } from 'src/hooks/useBookingSummary';
import { QueryClientAccessor } from 'src/redux/QueryClientReduxCompatible';

import { VehicleProtection } from '../vehicleProtections/types';
import { Extras } from '../extras/types';

import {
    ProductDataState,
    UpdateProductPayload,
    UpdateProductsPayload,
    ProductsResponse,
} from './types';
import { productGetAction, productUpdateAction } from './products.duck';
import * as api from './products.api';
import {
    vehicleProtectionInsurancesSelector,
    vehicleProtectionUpgradesSelector,
    extrasSelector,
} from './products.selector';

const apiCalls = SCIApi.getInstance();

const mapProductResponse = (
    productResponse: ProductsResponse,
): ProductDataState => ({
    insurances: [
        ...(productResponse?.insurances?.standardLiabilities ?? []),
        ...(productResponse?.insurances?.reducedLiabilities ?? []),
    ].sort((i1, i2) => i1.order - i2.order),
    upgrade: productResponse?.insurances?.optionalLiabilityUpgrades?.[0],
    extras: productResponse?.extras,
});

function* getProducts() {
    try {
        const data: ProductsResponse = yield apiCalls.get<ProductsResponse>(
            api.productsApiRoute,
        );

        yield put(productGetAction.success(mapProductResponse(data)));
        QueryClientAccessor.getInstance()?.refetchQueries({
            queryKey: [useBookingSummaryKey],
        });
    } catch (error) {
        yield put(productGetAction.failure(error));
    }
}

const mapExtrasToPayload = (
    extras: Extras,
    productUpdating?: UpdateProductPayload,
): UpdateProductPayload[] => {
    return Object.keys(extras).reduce(
        (products: UpdateProductPayload[], extraCategoryKey: string) => {
            const productFromCategory = extras[extraCategoryKey].products
                .map((product) => ({
                    ...product,
                    quantity:
                        product.id.productId === productUpdating?.id?.productId
                            ? productUpdating.quantity
                            : product.quantity,
                }))
                .filter((p) => p.quantity > 0);

            return [...products, ...productFromCategory];
        },
        [],
    );
};

const mapPayload = (
    product: UpdateProductPayload,
    insurances: VehicleProtection[],
    extras: Extras,
    upgrade?: VehicleProtection,
): UpdateProductsPayload => {
    const isChangingInsurance = !!insurances.find(
        (i) => i.id.productId === product.id.productId,
    );
    const isChangingUpgrade = upgrade?.id?.productId === product.id.productId;

    let newUpgrade = isChangingUpgrade ? product : upgrade;
    if (!newUpgrade || newUpgrade.quantity <= 0) {
        newUpgrade = undefined;
    }

    const products: UpdateProductPayload[] = [
        ...(!isChangingInsurance
            ? insurances.filter((i) => i.quantity > 0)
            : [product]),
        ...(!!newUpgrade ? [newUpgrade] : []),
        ...mapExtrasToPayload(extras, product),
    ];

    return { products: products };
};

function* updateProducts({ payload }: { payload: UpdateProductPayload }) {
    try {
        const insurances: VehicleProtection[] = yield select(
            // Error with current reselect, we need consider ts is wrong here.
            // eslint-disable-next-line
            // @ts-ignore
            vehicleProtectionInsurancesSelector,
        );

        const upgrade: VehicleProtection = yield select(
            // Error with current reselect, we need consider ts is wrong here.
            // eslint-disable-next-line
            // @ts-ignore
            vehicleProtectionUpgradesSelector,
        );

        const extras: Extras = yield select(
            // Error with current reselect, we need consider ts is wrong here.
            // eslint-disable-next-line
            // @ts-ignore
            extrasSelector,
        );

        const mappedPayload = mapPayload(payload, insurances, extras, upgrade);

        const data: ProductsResponse = yield apiCalls.post<ProductsResponse>(
            api.productsApiRoute,
            mappedPayload,
        );

        yield put(productGetAction.success(mapProductResponse(data)));

        QueryClientAccessor.getInstance()?.refetchQueries({
            queryKey: [useBookingSummaryKey],
        });
    } catch (error) {
        yield put(productGetAction.failure(error));
    }
}

function* productsSaga() {
    yield all([takeLatest(productGetAction, getProducts)]);
    yield all([takeLatest(productUpdateAction, updateProducts)]);
}

export default productsSaga;
